import { ZegoExpressEngine } from "zego-express-engine-webrtc";

import { log } from "@/lib/logger";

/**
 * Thin wrapper around the ZegoCloud Web SDK.
 *
 * Owns exactly ONE engine instance, one local stream and one set of room
 * listeners per call. Backend-provided credentials are consumed verbatim —
 * nothing here generates a room id, a user id or a token.
 */

const ZEGO_SERVER = "wss://webliveroom-api.zego.im/ws";
/** publishQualityUpdate fires every ~3s; one sample per interval is plenty. */
const QUALITY_LOG_INTERVAL_MS = 15_000;

export interface ZegoCredentials {
  appId: number;
  token: string;
  roomId: string;
  userId: string;
  userName: string;
}

export interface ZegoEngineHandlers {
  onRemoteStream: (stream: MediaStream | null) => void;
  onRemoteUserPresent: (present: boolean) => void;
  onRoomState: (state: "CONNECTED" | "CONNECTING" | "DISCONNECTED") => void;
  onTokenWillExpire: () => void;
  /** The doctor's own stream stopped reaching Zego — the patient sees/hears nothing. */
  onPublishFailed: (errorCode: number) => void;
  /** Called when the local stream is ready for recording (after publishing starts). */
  onLocalStreamReady?: (stream: MediaStream) => void;
}

type AnyStream = {
  /** Zego mounts its own <video> INSIDE this container — never pass a <video>. */
  playVideo?: (container: HTMLElement, opts?: unknown) => void;
  stream?: MediaStream;
} & MediaStream;

/** Media-device failures we can explain to the doctor. */
export type MediaErrorKind = "permission" | "device" | "unknown";

export function classifyMediaError(error: unknown): MediaErrorKind {
  const name = (error as { name?: string; errorCode?: number } | null)?.name;
  if (name === "NotAllowedError" || name === "PermissionDeniedError") return "permission";
  if (name === "NotFoundError" || name === "DevicesNotFoundError") return "device";
  return "unknown";
}

export class ZegoCallEngine {
  private engine: ZegoExpressEngine | null = null;
  private localStream: AnyStream | null = null;
  private remotePlaying = new Set<string>();
  private credentials: ZegoCredentials | null = null;
  private handlers: ZegoEngineHandlers;
  private destroyed = false;
  /**
   * Serialises camera device changes. Toggling the hardware is async and slow
   * enough that a doctor can press the button again before the first change
   * lands; without a queue the two calls race and the camera can end up in the
   * opposite state to the button.
   */
  private cameraQueue: Promise<void> = Promise.resolve();
  private cameraGeneration = 0;
  /**
   * The container the UI has asked us to render the self-view into. Recorded
   * even before the camera exists — see `attachLocal`.
   */
  private requestedView: HTMLElement | null = null;
  /** The container the SDK is actually mounted on; a stream may only have one. */
  private mountedView: HTMLElement | null = null;
  private lastQualityLog = 0;

  constructor(handlers: ZegoEngineHandlers) {
    this.handlers = handlers;
  }

  get publishStreamId(): string | null {
    return this.credentials ? `${this.credentials.userId}_stream` : null;
  }

  /** Returns the local MediaStream being published (for recording). */
  getLocalStream(): MediaStream | null {
    return this.localStream ? (this.localStream as unknown as MediaStream) : null;
  }

  /** Logs into the backend-generated room and registers the single listener set. */
  async join(credentials: ZegoCredentials): Promise<void> {
    if (this.engine) await this.teardown();
    this.destroyed = false;
    this.credentials = credentials;

    const engine = new ZegoExpressEngine(credentials.appId, ZEGO_SERVER);
    this.engine = engine;
    this.registerListeners(engine);

    const joined = await engine.loginRoom(
      credentials.roomId,
      credentials.token,
      { userID: credentials.userId, userName: credentials.userName },
      // roomUserUpdate does NOT fire unless the room is opened with userUpdate,
      // and the "waiting for patient" indicator is driven by it.
      { userUpdate: true },
    );
    if (!joined) throw new Error("Could not join the consultation room.");
    log.info("call", "room.joined", { roomId: credentials.roomId, userId: credentials.userId });
  }

  /** Creates + publishes the doctor's camera/mic. Throws on permission/device failure. */
  async startLocalStream(view: HTMLElement | null): Promise<void> {
    const engine = this.engine;
    const credentials = this.credentials;
    if (!engine || !credentials) throw new Error("Not connected to the call room.");

    const stream = (await engine.createZegoStream({
      camera: { audio: true, video: true },
    })) as unknown as AnyStream;
    this.localStream = stream;

    // A camera-only or hardware-muted capture publishes a silent stream, which
    // looks like a working call to the doctor while the patient hears nothing.
    const audio = stream.getAudioTracks?.() ?? [];
    const video = stream.getVideoTracks?.() ?? [];
    log.info("call", "capture.ready", {
      audioTracks: audio.length,
      videoTracks: video.length,
      // `muted` here is the browser's "source is producing no data" flag, not
      // our UI mute — true means the mic is captured but silent.
      mic: audio[0]
        ? { device: audio[0].label, enabled: audio[0].enabled, silent: audio[0].muted }
        : null,
    });
    if (audio.length === 0) {
      log.error("call", "capture.noAudioTrack", {
        msg: "Camera captured without a microphone track — the patient will hear nothing.",
      });
    }

    // A null view here is normal — the stage may not be mounted yet. Never let
    // it clear a container the UI already registered; just mount what we have.
    if (view) this.attachLocal(view);
    else this.mountLocalPreview();

    // Synchronous and boolean-returning: a false here is a failed publish that
    // throws nothing, so it must be checked rather than awaited.
    const publishing = engine.startPublishingStream(
      `${credentials.userId}_stream`,
      stream as unknown as MediaStream,
    );
    if (!publishing) {
      log.error("call", "publish.rejected", { streamId: `${credentials.userId}_stream` });
      throw new Error("Could not start sending your camera and microphone.");
    }

    // Notify that the local stream is ready for recording (after successful publish).
    // The stream is the same one being published, so recording it captures exactly
    // what the patient hears — no second microphone session.
    //
    // `createZegoStream` returns a ZegoLocalStream wrapper in newer SDK builds, whose
    // real tracks live on `.stream` (see `stopAllTracks`) — MediaRecorder rejects
    // that wrapper outright ("parameter 1 is not of type 'MediaStream'"), so the
    // native MediaStream must be unwrapped before handing it to the recorder.
    this.handlers.onLocalStreamReady?.((stream.stream ?? stream) as MediaStream);
  }

  /**
   * Registers the container the self-view should render into.
   *
   * Deliberately separate from mounting: the container and the camera become
   * available in either order, and whichever arrives second must complete the
   * job. Recording the request and mounting when both exist is what stops the
   * self-view being lost when the patient is already in the room — see the
   * `preview.deferred` note in FRONTEND.MD §9.
   *
   * `view` is a CONTAINER element: `playVideo` builds its own <video> inside it
   * (and handles autoplay policy). Passing a <video> silently renders nothing,
   * because a child appended to <video> is treated as fallback content.
   */
  attachLocal(view: HTMLElement | null): void {
    this.requestedView = view;
    this.mountLocalPreview();
  }

  /** Mounts the self-view once BOTH the container and the camera stream exist. */
  private mountLocalPreview(): void {
    const view = this.requestedView;
    const stream = this.localStream;
    if (!view || !stream) {
      if (view && !stream) log.debug("call", "preview.deferred", { reason: "no-stream-yet" });
      return;
    }
    if (this.mountedView === view) return;
    this.mountedView = view;

    try {
      if (typeof stream.playVideo === "function") {
        stream.playVideo(view, { objectFit: "cover", mirror: true });
      } else {
        this.mountFallbackVideo(view, stream);
      }
    } catch (error) {
      log.error("call", "preview.failed", {
        error: error instanceof Error ? error.message : String(error),
      });
      this.mountFallbackVideo(view, stream);
    }

    // The SDK renders into a wrapper it creates itself; if that wrapper never
    // appeared, the doctor is looking at an empty box while publishing happily.
    // Say so in the log rather than leaving a silent blank.
    const mounted = Boolean(view.querySelector("video"));
    log[mounted ? "info" : "error"]("call", mounted ? "preview.mounted" : "preview.empty", {
      via: typeof stream.playVideo === "function" ? "playVideo" : "fallback",
    });
    if (!mounted) this.mountFallbackVideo(view, stream);
  }

  /**
   * Forces the self-view to rebuild against the current camera track.
   *
   * `mountLocalPreview` is idempotent by design — it returns early when the
   * container is already mounted — so re-acquiring the camera needs this to get
   * past that guard. The container is emptied first: `playVideo` builds its own
   * wrapper each time, and without the clear every camera toggle would stack
   * another dead <video> in the box.
   */
  private remountLocalPreview(): void {
    const view = this.requestedView;
    if (!view || !this.localStream) return;
    view.replaceChildren();
    this.mountedView = null;
    this.mountLocalPreview();
  }

  /** SDK builds without playVideo, or a playVideo that rendered nothing. */
  private mountFallbackVideo(view: HTMLElement, stream: AnyStream): void {
    const existing = view.querySelector("video");
    const video = existing ?? view.appendChild(document.createElement("video"));
    video.autoplay = true;
    video.playsInline = true;
    video.muted = true; // never play the doctor's own mic back at them
    video.style.width = "100%";
    video.style.height = "100%";
    video.style.objectFit = "cover";
    video.srcObject = (stream.stream ?? stream) as MediaStream;
    void video.play().catch(() => undefined);
  }

  setMicrophoneMuted(muted: boolean): void {
    if (!this.engine || !this.localStream) return;
    const ok = this.engine.mutePublishStreamAudio(
      this.localStream as unknown as MediaStream,
      muted,
    );
    log.debug("call", "publish.audioMute", { muted, ok });
  }

  /**
   * Turns the camera off, at the hardware — not just on the wire.
   *
   * `mutePublishStreamVideo` alone stops frames reaching the patient but leaves
   * the capture device open, so the webcam indicator light stays on and the
   * browser still reports the tab as using the camera. The doctor is told
   * "Camera off" while their camera is demonstrably still running, which is a
   * privacy problem rather than a cosmetic one — so the device is released too.
   *
   * The two calls are ordered by which failure is worse:
   *
   *   OFF  mute first, synchronously, then release the device. Muting is
   *        instant, so nothing reaches the patient during the hundreds of
   *        milliseconds the hardware takes to let go.
   *   ON   re-acquire the device first, then unmute. Unmuting a device that has
   *        not resumed publishes black frames.
   *
   * Zego's own docs recommend the mute-only path because toggling the hardware
   * is slow. That is a performance argument, and it loses to the doctor's
   * reasonable belief that "off" means off.
   */
  setCameraOff(off: boolean): void {
    if (!this.engine || !this.localStream) return;
    const engine = this.engine;
    const stream = this.localStream as unknown as MediaStream;

    // Every toggle takes a ticket. A rapid off/on/off must settle on the last
    // request, not on whichever async device call happens to finish last.
    const generation = ++this.cameraGeneration;
    const superseded = () => generation !== this.cameraGeneration || this.destroyed;

    if (off) this.mutePublishVideo(engine, stream, true);

    this.cameraQueue = this.cameraQueue.then(async () => {
      if (superseded()) return;

      let applied = false;
      try {
        applied = await engine.enableVideoCaptureDevice(stream, !off);
      } catch (error) {
        log.warn("call", "capture.videoDeviceFailed", {
          enabled: !off,
          error: error instanceof Error ? error.message : String(error),
        });
      }

      if (superseded()) return;

      if (!off) {
        this.mutePublishVideo(engine, stream, false);
        // Releasing the device stopped the track the self-view was bound to, so
        // the preview would otherwise come back as a black box. Re-mounting is
        // cheap and self-correcting; leaving it is a blank preview while the
        // patient sees the doctor perfectly — the exact confusing signature
        // FRONTEND.MD §9 warns about.
        this.remountLocalPreview();
      }

      if (off && !applied) {
        // Publishing is muted so the patient sees nothing, but the hardware is
        // still held and the light is still on. Logged loudly: the UI is now
        // saying something about the doctor's camera that is not true.
        log.error("call", "capture.videoDeviceStuck", {
          msg: "Camera muted but the capture device was not released — the indicator light stays on.",
        });
        return;
      }

      log.info("call", "capture.videoDevice", { enabled: !off });
    });
  }

  private mutePublishVideo(engine: ZegoExpressEngine, stream: MediaStream, mute: boolean): void {
    const ok = engine.mutePublishStreamVideo(stream, mute);
    log.debug("call", "publish.videoMute", { off: mute, ok });
  }

  /**
   * Stops every underlying track, whatever wrapper the SDK handed back.
   *
   * `createZegoStream` returns a ZegoLocalStream in newer builds and a plain
   * MediaStream in older ones, so the real tracks live either on the object
   * itself or on its `.stream`. Both are checked — missing one would leave the
   * device open, which is exactly the failure this guards against.
   */
  private stopAllTracks(stream: AnyStream | null): void {
    if (!stream) return;
    const candidates = [stream, stream.stream].filter(Boolean) as MediaStream[];
    let stopped = 0;
    for (const candidate of candidates) {
      const tracks = candidate.getTracks?.() ?? [];
      for (const track of tracks) {
        try {
          track.stop();
          stopped += 1;
        } catch {
          /* already stopped */
        }
      }
    }
    log.info("call", "capture.released", { tracksStopped: stopped });
  }

  /** Applies a freshly minted backend token to the live room. */
  async renewToken(token: string): Promise<void> {
    if (!this.engine || !this.credentials) return;
    this.credentials = { ...this.credentials, token };
    await this.engine.renewToken(this.credentials.roomId, token);
  }

  /** Releases camera/mic, stops publishing, leaves the room, removes listeners. */
  async teardown(): Promise<void> {
    const engine = this.engine;
    const credentials = this.credentials;
    this.destroyed = true;
    this.engine = null;
    this.requestedView = null;
    this.mountedView = null;
    if (!engine) return;

    try {
      for (const streamId of this.remotePlaying) {
        try {
          engine.stopPlayingStream(streamId);
        } catch {
          /* already stopped */
        }
      }
      this.remotePlaying.clear();

      if (credentials) {
        try {
          engine.stopPublishingStream(`${credentials.userId}_stream`);
        } catch {
          /* not publishing */
        }
      }
      if (this.localStream) {
        try {
          engine.destroyStream(this.localStream as unknown as MediaStream);
        } catch {
          /* already destroyed */
        }
        // Belt and braces. `destroyStream` is supposed to release the devices,
        // but the camera light staying on after a call is the single most
        // alarming thing this app can do to a doctor — so the tracks are
        // stopped explicitly rather than on the SDK's word. Stopping an
        // already-stopped track is a no-op, so this is safe either way.
        this.stopAllTracks(this.localStream);
        this.localStream = null;
      }
      if (credentials) {
        try {
          await engine.logoutRoom(credentials.roomId);
        } catch {
          /* already out */
        }
      }
    } finally {
      try {
        engine.off("roomStreamUpdate");
        engine.off("roomUserUpdate");
        engine.off("roomStateUpdate");
        engine.off("publisherStateUpdate");
        engine.off("publishQualityUpdate");
        engine.off("tokenWillExpire");
      } catch {
        /* listeners already gone */
      }
      try {
        engine.destroyEngine();
      } catch {
        /* noop */
      }
      this.handlers.onRemoteStream(null);
    }
  }

  private registerListeners(engine: ZegoExpressEngine): void {
    engine.on("roomStreamUpdate", (_roomId, updateType, streamList) => {
      if (this.destroyed) return;
      void (async () => {
        if (updateType === "ADD") {
          for (const item of streamList) {
            try {
              const remote = (await engine.startPlayingStream(
                item.streamID,
              )) as unknown as MediaStream;
              this.remotePlaying.add(item.streamID);
              this.handlers.onRemoteStream(remote);
            } catch {
              /* the stream vanished before we could play it */
            }
          }
          return;
        }
        for (const item of streamList) {
          try {
            engine.stopPlayingStream(item.streamID);
          } catch {
            /* already stopped */
          }
          this.remotePlaying.delete(item.streamID);
        }
        if (this.remotePlaying.size === 0) this.handlers.onRemoteStream(null);
      })();
    });

    engine.on("roomUserUpdate", (_roomId, updateType, userList) => {
      if (this.destroyed || userList.length === 0) return;
      this.handlers.onRemoteUserPresent(updateType === "ADD");
    });

    engine.on("roomStateUpdate", (_roomId, state) => {
      if (this.destroyed) return;
      this.handlers.onRoomState(state);
    });

    // Publishing is asynchronous after startPublishingStream returns, so this
    // is the only place a rejected or stalled publish becomes visible.
    engine.on("publisherStateUpdate", ({ streamID, state, errorCode }) => {
      if (this.destroyed) return;
      log[state === "PUBLISHING" ? "info" : errorCode ? "error" : "debug"](
        "call",
        "publish.state",
        {
          streamID,
          state,
          errorCode,
        },
      );
      // NO_PUBLISH with an error code means Zego rejected or dropped our stream
      // (a token without publish privilege reports itself here). Without this
      // the call looks healthy to the doctor while the patient gets nothing.
      if (state === "NO_PUBLISH" && errorCode) this.handlers.onPublishFailed(errorCode);
    });

    // Answers "is the doctor's microphone actually reaching Zego?" — audioBitrate
    // sitting at 0, or muteState "1", localises a silent call to this side.
    engine.on("publishQualityUpdate", (streamID, stats) => {
      if (this.destroyed) return;
      const now = Date.now();
      if (now - this.lastQualityLog < QUALITY_LOG_INTERVAL_MS) return;
      this.lastQualityLog = now;
      log.debug("call", "publish.quality", {
        streamID,
        audioBitrate: Math.round(stats.audio?.audioBitrate ?? 0),
        audioMuted: stats.audio?.muteState === "1",
        videoBitrate: Math.round(stats.video?.videoBitrate ?? 0),
      });
    });

    engine.on("tokenWillExpire", () => {
      if (this.destroyed) return;
      this.handlers.onTokenWillExpire();
    });
  }
}
